import React from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaEdit, FaLock, FaPlus, FaTrashAlt, FaUnlock } from "react-icons/fa";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { NS, U3129ROLES } from "../../U3129";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3129Timesheet, U3129TimesheetStatus } from "../../U3129Service";

interface U3129TimesheetFormToolBarProps {
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
  /** BL-0656 — masque la toolbar métier (Enregistrer/Annuler via U2000DefaultForm). */
  approvalCorrectionMode?: boolean;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  /** Soumission pour approbation (Open → WaitingForApproval). */
  onCloseTimesheet?: () => void;
  /** True pendant l’appel POST submitForApproval. */
  closeTimesheetLoading?: boolean;
  /** Réouverture (WaitingForApproval → Open). */
  onReopenTimesheet?: () => void;
  /** True pendant l’appel POST reopen. */
  reopenTimesheetLoading?: boolean;
}

const U3129TimesheetFormToolBar: React.FC<U3129TimesheetFormToolBarProps> = ({
  formStateHandlers,
  approvalCorrectionMode = false,
  onAdd,
  onModify,
  onDelete,
  onCloseTimesheet,
  closeTimesheetLoading = false,
  onReopenTimesheet,
  reopenTimesheetLoading = false,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const formState = formStateHandlers.getFormState();
  const data = formStateHandlers.getData();
  const timesheetStatus = data?.status as U3129TimesheetStatus | undefined;
  const timesheetId = data?.id?.trim() ?? "";
  const hasPersistedId = timesheetId.length > 0;
  const isOpen = timesheetStatus === "Open";
  const isWaitingForApproval = timesheetStatus === "WaitingForApproval";

  if (approvalCorrectionMode) {
    return null;
  }

  if (formState === U2000FormStateEnumState.EDIT) {
    return null;
  }

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3129ROLES.ADD),
      disabled: formState === U2000FormStateEnumState.EDIT,
      handleClick: onAdd,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3129ROLES.EDIT),
      visible: hasPersistedId && isOpen,
      disabled: formState === U2000FormStateEnumState.EDIT,
      handleClick: onModify,
      icon: FaEdit,
      Component: U2000ActionBtn,
    },
    {
      key: "closeTimesheet",
      label: closeTimesheetLoading ? t("btnCloseTimesheetPending", { ns: NS }) : t("btnCloseTimesheet", { ns: NS }),
      hasRole: hasRole(U3129ROLES.EDIT),
      visible: Boolean(onCloseTimesheet) && hasPersistedId && isOpen,
      disabled: formState === U2000FormStateEnumState.EDIT || closeTimesheetLoading || reopenTimesheetLoading,
      handleClick: () => onCloseTimesheet?.(),
      icon: FaLock,
      className: "bg-white! text-green-700! border! border-green-300! hover:bg-green-50! hover:shadow-none! focus:ring-green-500!",
      Component: U2000ActionBtn,
    },
    {
      key: "reopenTimesheet",
      label: reopenTimesheetLoading ? t("btnReopenTimesheetPending", { ns: NS }) : t("btnReopenTimesheet", { ns: NS }),
      hasRole: hasRole(U3129ROLES.EDIT),
      visible: Boolean(onReopenTimesheet) && hasPersistedId && isWaitingForApproval,
      disabled: formState === U2000FormStateEnumState.EDIT || closeTimesheetLoading || reopenTimesheetLoading,
      handleClick: () => onReopenTimesheet?.(),
      icon: FaUnlock,
      className: "bg-white! text-amber-700! border! border-amber-300! hover:bg-amber-50! hover:shadow-none! focus:ring-amber-500!",
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3129ROLES.DELETE),
      visible: hasPersistedId && isOpen,
      disabled: formState === U2000FormStateEnumState.EDIT,
      handleClick: onDelete,
      icon: FaTrashAlt,
      className: "bg-white! text-red-600! border! border-red-300! hover:bg-red-50! hover:shadow-none! focus:ring-red-500!",
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};

export default U3129TimesheetFormToolBar;
